import { Workspace } from '@sr-sdks/api-sdk-axios';
import { Path } from 'react-hook-form';
import { ValidationError } from 'yup';
import { workspaceToEntityFormDefaults } from './defaults';
import { workspaceDetailsMembershipSchema } from './schema';
import { WorkspaceDetailsFormValues } from './types';

type MembershipDetailError = {
  message: string;
  name: Path<WorkspaceDetailsFormValues>;
};

/**
 * What an application still needs from these form values, one entry per
 * field, with the message to show against it.
 */
export const getMembershipDetailErrors = (
  values: WorkspaceDetailsFormValues,
): MembershipDetailError[] => {
  try {
    workspaceDetailsMembershipSchema.validateSync(values, {
      abortEarly: false,
    });

    return [];
  } catch (error) {
    if (!(error instanceof ValidationError)) throw error;

    const errorsByField = new Map<string, MembershipDetailError>();

    error.inner.forEach(({ message, path }) => {
      if (path && !errorsByField.has(path)) {
        errorsByField.set(path, {
          message,
          name: path as Path<WorkspaceDetailsFormValues>,
        });
      }
    });

    return [...errorsByField.values()];
  }
};

// Keyed on the query's workspace object, which only changes when its data does,
// so every task card on a page shares one validation.
const readinessByWorkspace = new WeakMap<Workspace, boolean>();

/** Judges the saved workspace, not unsaved edits: Apply sends what the API holds. */
export const isWorkspaceReadyForMembership = (workspace: Workspace) => {
  const cached = readinessByWorkspace.get(workspace);

  if (cached !== undefined) return cached;

  const isReady = workspaceDetailsMembershipSchema.isValidSync(
    workspaceToEntityFormDefaults(workspace),
  );

  readinessByWorkspace.set(workspace, isReady);

  return isReady;
};
